import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SendIcon from '@mui/icons-material/Send';
import { requestDemo } from '../services/homeService';
import { toast } from 'react-toastify';

const RequestDemoForm = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    vatNumber: '',
    phoneNumber: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vatNumber.trim()) {
      newErrors.vatNumber = t('field_required');
    } else if (!/^(BE)?[0-9]{10}$/i.test(formData.vatNumber.replace(/\s/g, ''))) {
      newErrors.vatNumber = t('invalid_vat');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('field_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalid_email');
    }

    if (formData.message.length > 100) {
      newErrors.message = t('message_max_length');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await requestDemo(formData);
      toast.success(t('demo_request_success'));
      setFormData({
        companyName: '',
        vatNumber: '',
        phoneNumber: '',
        email: '',
        message: '',
      });
      setErrors({});
    } catch (error) {
      toast.error(t('demo_request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      id="demo"
      sx={{
        py: 10,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? theme.palette.background.paper 
              : '#fff',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              {t('demo_form_title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              {t('demo_form_subtitle')}
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('company_name')}
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('vat_number')}
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleChange}
                  error={!!errors.vatNumber}
                  helperText={errors.vatNumber}
                  variant="outlined"
                  placeholder="BE0123456789"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('phone_number')}
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="+32 2 123 4567"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('email_address')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('demo_message')}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={
                    errors.message ||
                    `${formData.message.length}/100 ${t('message').toLowerCase()}`
                  }
                  variant="outlined"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  endIcon={<SendIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  {isSubmitting ? t('loading') || 'Submitting...' : t('submit_request')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default RequestDemoForm;
